import { useState, useEffect, useCallback } from 'react';
import { ComprehensiveStockAnalysis } from '@/types/perplexity.types';
import { supabase } from '@services/supabase/supabaseClient';
import { isTopTicker } from '@/constants/topTickers';

interface UseComprehensiveAnalysisReturn {
  analysis: ComprehensiveStockAnalysis | null;
  isLoading: boolean;
  error: string | null;
  source: 'session' | 'database' | 'api' | null;
  cacheAge: string | null;
}

// Cache settings
const CACHE_DURATION = 12 * 60 * 60 * 1000; // 12 hours
const LOADING_MESSAGE = 'Analyzing fundamentals, technicals, and market sentiment...';

// In-memory session cache
const sessionCache: Map<string, { data: ComprehensiveStockAnalysis; timestamp: number }> =
  new Map();

/**
 * Hook for fetching comprehensive stock analysis with 3-tier caching
 *
 * Caching strategy:
 * 1. Session cache (in-memory, fastest)
 * 2. Database cache (pre-fetched by cron for top 20, or on-demand for others)
 * 3. Edge function (calls get-comprehensive-analysis which handles API + caching)
 *
 * @param ticker - Stock ticker symbol
 * @param companyName - Company name
 * @returns Comprehensive analysis data, loading state, error, source, and cache age
 */
export const useComprehensiveAnalysis = (
  ticker: string,
  companyName: string
): UseComprehensiveAnalysisReturn => {
  const [analysis, setAnalysis] = useState<ComprehensiveStockAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<'session' | 'database' | 'api' | null>(null);
  const [cacheAge, setCacheAge] = useState<string | null>(null);

  const fetchAnalysis = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const now = Date.now();

      // Step 1: Check session cache first (fastest)
      if (sessionCache.has(ticker)) {
        const cached = sessionCache.get(ticker)!;
        if (now - cached.timestamp < CACHE_DURATION) {
          const ageHours = Math.round((now - cached.timestamp) / (60 * 60 * 1000));
          console.log(
            `[useComprehensiveAnalysis] Using session cache for ${ticker} (age: ${ageHours}h)`
          );
          setAnalysis(cached.data);
          setSource('session');
          setCacheAge(`${ageHours}h`);
          setIsLoading(false);
          return;
        }
      }

      // Step 2: If top-20 ticker, check database cache (pre-fetched by cron)
      if (isTopTicker(ticker)) {
        console.log(`[useComprehensiveAnalysis] Checking database cache for ${ticker}...`);
        const { data: dbCache, error: dbError } = await supabase
          .from('stock_comprehensive_analysis_cache')
          .select('analysis_data, fetched_at')
          .eq('ticker', ticker.toUpperCase())
          .single();

        if (!dbError && dbCache) {
          const fetchedAt = new Date(dbCache.fetched_at).getTime();
          const age = now - fetchedAt;

          if (age < CACHE_DURATION) {
            const ageHours = Math.round(age / (60 * 60 * 1000));
            console.log(
              `[useComprehensiveAnalysis] Using database cache for ${ticker} (age: ${ageHours}h)`
            );

            const analysisData = dbCache.analysis_data as ComprehensiveStockAnalysis;

            // Update session cache
            sessionCache.set(ticker, {
              data: analysisData,
              timestamp: fetchedAt,
            });

            setAnalysis(analysisData);
            setSource('database');
            setCacheAge(`${ageHours}h`);
            setIsLoading(false);
            return;
          } else {
            console.log(
              `[useComprehensiveAnalysis] Database cache stale for ${ticker} (age: ${Math.round(age / (60 * 60 * 1000))}h)`
            );
          }
        }
      }

      // Step 3: Fallback to edge function (handles on-demand caching)
      console.log(`[useComprehensiveAnalysis] ${LOADING_MESSAGE} (${ticker})`);
      const { data: edgeResponse, error: edgeError } = await supabase.functions.invoke(
        'get-comprehensive-analysis',
        {
          body: { ticker, companyName },
        }
      );

      if (edgeError) {
        // Check if edge function is not deployed
        if (edgeError.message?.includes('404') || edgeError.message?.includes('Not Found')) {
          throw new Error(
            'Edge function not deployed yet. Please deploy get-comprehensive-analysis function first.'
          );
        }
        throw new Error(`Edge function error: ${edgeError.message}`);
      }

      if (edgeResponse?.error) {
        throw new Error(`API error: ${edgeResponse.error}`);
      }

      if (edgeResponse?.data) {
        const analysisData = edgeResponse.data as ComprehensiveStockAnalysis;
        const edgeSource = edgeResponse.source as 'cache' | 'api';
        const edgeCacheAge = edgeResponse.cacheAge as string;

        // Update session cache
        sessionCache.set(ticker, {
          data: analysisData,
          timestamp: now,
        });

        setAnalysis(analysisData);
        setSource(edgeSource === 'cache' ? 'database' : 'api');
        setCacheAge(edgeCacheAge);
        console.log(
          `[useComprehensiveAnalysis] Analysis for ${ticker} from ${edgeSource} (age: ${edgeCacheAge})`
        );
      } else {
        throw new Error('No data returned from edge function');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch comprehensive analysis';
      setError(errorMessage);
      console.error(`[useComprehensiveAnalysis] Error for ${ticker}:`, errorMessage);
      console.error('Full error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [ticker, companyName]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  return {
    analysis,
    isLoading,
    error,
    source,
    cacheAge,
  };
};
